/* global process */
import Appsignal from "@appsignal/javascript";

// Errors whose stack frames originate from third-party scripts or browser extensions we
// can't act on. AppSignal has no built-in URL deny-list, so we inspect each error's
// backtrace in an override below and discard the span when any frame matches. These are
// substring matchers, not anchored ones: AppSignal's backtrace lines wrap each URL in
// browser-specific stack frame syntax (e.g. `at fn (chrome-extension://…)`), so the URL
// must be matched wherever it sits in the line.
const DENIED_BACKTRACE_PATTERNS = [
  // Facebook flakiness / blocked SDK
  /graph\.facebook\.com/i,
  /connect\.facebook\.net\/en_US\/all\.js/i,
  // Woopra flakiness
  /eatdifferent\.com\.woopra-ns\.com/i,
  /static\.woopra\.com\/js\/woopra\.js/i,
  // Browser extensions
  /extensions\//i,
  /chrome:\/\//i,
  /chrome-extension:\/\//i,
  // Other third-party plugins
  /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
  /webappstoolbarba\.texthelp\.com\//i,
  /metrics\.itunes\.apple\.com\.edgesuite\.net\//i
];

const hasDeniedBacktrace = (backtrace = []) =>
  backtrace.some(line => DENIED_BACKTRACE_PATTERNS.some(pattern => pattern.test(line)));

const appsignal = new Appsignal({
  key: process.env.APPSIGNAL_FRONTEND_API_KEY,
  revision: process.env.SOURCE_VERSION,
  ignoreErrors: [
    // Errors with no possible reproduction
    /^No error message$/i,
    /\?\(webkit-masked-url:\/\/hidden\/\)/,
    /NotReadableError: The I\/O read operation failed\./,
    // Errors thrown when the user navigates away before a request completes
    /^Network request failed$/i,
    /^Aborted$/i,
    /AbortError: The operation was aborted/,
    // Error thrown when the user denies access to a media device
    /The request is not allowed by the user agent or the platform in the current context/,
    // Errors from Firefox extensions
    /moz-extension:\/\//
  ]
});

// Returning `false` from an override discards the span before it is sent.
appsignal.addOverride(span => {
  const { backtrace } = span.getError() || {};

  return hasDeniedBacktrace(backtrace) ? false : span;
});

window.addEventListener("error", ({ error, message }) => {
  if (error instanceof Error) {
    appsignal.sendError(error);
  } else if (message) {
    // Non-Error values thrown (e.g. `throw "string"`) — wrap so AppSignal can accept them.
    appsignal.sendError(new Error(message));
  }
});

window.addEventListener("unhandledrejection", ({ reason }) => {
  if (reason instanceof Error) {
    appsignal.sendError(reason);
  } else if (reason != null) {
    // Non-Error promise rejections (plain objects, strings, etc.) — derive a message and wrap.
    const message = reason?.message ?? String(reason);
    appsignal.sendError(new Error(message));
  }
});

export default appsignal;
